// GeneralSettings.tsx
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SecurityIcon from "@mui/icons-material/Security";
import EditIcon from "@mui/icons-material/Edit";
import { Application } from "@microsoft/microsoft-graph-types";
import { useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Client } from "@microsoft/microsoft-graph-client";

interface GeneralSettingsProps {
  app: Application;
  modifiedApp: Application;
  setModifiedApp: React.Dispatch<React.SetStateAction<Application>>;
  spLoading: boolean;
  servicePrincipalId: string | null;
  tenantId: string;

  handleCopyToClipboard: (text: string) => void;
  setClientCredsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  updateApplication: () => void;
  graphAccessToken: string;  // Add this for the platform update API calls
  onAppUpdated: (updatedApp: Application) => void; // Callback when app is updated
}

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  app,
  modifiedApp,
  setModifiedApp,
  spLoading,
  servicePrincipalId,
  tenantId,
  handleCopyToClipboard,
  setClientCredsModalOpen,
  updateApplication,
  graphAccessToken,  // New prop
  onAppUpdated       // New prop
}) => {
  const hasWebUris = Boolean(modifiedApp.web?.redirectUris?.length);
  const hasSpaUris = Boolean(modifiedApp.spa?.redirectUris?.length);
  const [showSecurityTips, setShowSecurityTips] = useState(false);
  const [showIdentifierGuide, setShowIdentifierGuide] = useState(false);
  const [showIntegrationTips, setShowIntegrationTips] = useState(false);

  const [addingPlatform, setAddingPlatform] = useState(false);
  const [newPlatformUri, setNewPlatformUri] = useState("");
  const [platformError, setPlatformError] = useState("");
  
  // Determine which platforms exist and which can be added
  const hasWebPlatform = Boolean(modifiedApp.web?.redirectUris?.length);
  const hasSpaPlatform = Boolean(modifiedApp.spa?.redirectUris?.length);
  const [selectedPlatformType, setSelectedPlatformType] = useState<'web' | 'spa'>('web');

    // Function to add missing platform
    const handleAddPlatform = async (platformType: 'web' | 'spa') => {
      if (!newPlatformUri) {
        setPlatformError("Please provide a valid redirect URI");
        return;
      }
      
      if (!app.id || !graphAccessToken) {
        setPlatformError("Missing application ID or access token");
        return;
      }
      
      try {
        setPlatformError("");
        
        // Create patch body based on platform type
        const patchBody: Partial<Application> = {};
        
        if (platformType === 'web') {
          patchBody.web = {
            redirectUris: [newPlatformUri]
          };
        } else {
          patchBody.spa = {
            redirectUris: [newPlatformUri]
          };
        }
        
        const client = Client.init({
          authProvider: (done) => done(null, graphAccessToken),
        });
        
        // Update the application
        await client.api(`/applications/${app.id}`).patch(patchBody);
        
        // Create updated app object for local state update
        const updatedApp = { 
          ...modifiedApp,
          ...patchBody
        };
        
        // Update local state
        setModifiedApp(updatedApp);
        
        // Notify parent component
        if (onAppUpdated) {
          onAppUpdated(updatedApp);
        }
        
        // Reset UI state
        setAddingPlatform(false);
        setNewPlatformUri("");
        
        // Success notification could be added here
      } catch (error: any) {
        console.error("Error adding platform:", error);
        setPlatformError(error.message || "Failed to add platform");
      }
    };
    
    // Cancel adding platform
    const handleCancelAddPlatform = () => {
      setAddingPlatform(false);
      setNewPlatformUri("");
      setPlatformError("");
      setSelectedPlatformType('web'); // Reset to default
    };

  return (
    <>
      {/* 1) IDENTIFIERS */}
      <Accordion 
        defaultExpanded
        sx={{
          border: '1px solid #e8eaf6', // Light indigo border
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)', // Indigo gradient
            borderBottom: '1px solid #c5cae9',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5', // Indigo background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <VpnKeyIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593", // Dark indigo text
                letterSpacing: "0.01em"
              }}
            >
              Identifiers
            </Typography>
            <Chip 
              size="small"
              label="Core" 
              sx={{ 
                ml: 2,
                backgroundColor: '#c5cae9', // Light indigo background
                color: '#283593', // Dark indigo text
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}> {/* Light indigo background */}
          {/* Keep existing content but update container styling */}
          <Box sx={{ backgroundColor: "#fafbff", borderRadius: 1 }}> {/* Updated background */}
          <Typography variant="body1" sx={{ mb: 2 }}>
            These unique identifiers are used in authentication flows and API calls to reference your application.
          </Typography>

          {/* Visual explanation of identifiers */}
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            backgroundColor: '#f5f5f5', 
            border: '1px solid #e0e0e0', 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'flex-start'
          }}>
            <VpnKeyIcon sx={{ color: '#607d8b', mr: 2, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">
                <strong>Object ID:</strong> The unique identifier for this application in your Entra ID directory.<br />
                <strong>Application ID (clientId):</strong> Used in authentication requests and code. This is your "client_id".<br />
                <strong>Service Principal ID:</strong> The ID of the service instance in your tenant.<br />
                <strong>Tenant ID:</strong> The directory ID where this application is registered.
              </Typography>
              
              {/* Usage guidance link */}
              <Button 
                onClick={() => setShowIdentifierGuide(!showIdentifierGuide)}
                startIcon={<InfoOutlinedIcon fontSize="small" />}
                size="small"
                sx={{ mt: 1.5, textTransform: 'none' }}
                color="info"
              >
                {showIdentifierGuide ? 'Hide Usage Guide' : 'Show Usage Guide'}
              </Button>
            </Box>
          </Box>

          {/* Collapsible usage guide */}
          {showIdentifierGuide && (
            <Box sx={{ 
              mb: 3,
              p: 1.5, 
              backgroundColor: '#e3f2fd', 
              borderLeft: '4px solid #2196f3',
              borderRadius: '0 4px 4px 0'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                When to use each identifier:
              </Typography>
              <Typography variant="body2">
                • Use <strong>Object ID</strong> when managing the application via Graph API<br />
                • Use <strong>Application ID</strong> in authentication requests and code<br />
                • Use <strong>Service Principal ID</strong> for role assignments and tenant-specific configurations<br />
                • Use <strong>Tenant ID</strong> to specify the Entra ID directory
              </Typography>
            </Box>
          )}

            {/* Actual identifiers with copy buttons */}
            <Box sx={{ 
              mb: 3,
              p: 2,
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: 1
            }}>
              <Stack spacing={2}>
                {/* Display Name field */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Display Name:
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      width: {
                        xs: '60%',  // On extra small screens
                        sm: '70%'   // On small screens and larger
                      },
                      maxWidth: '70%' 
                    }}
                  >
                    <TextField
                      variant="standard"
                      value={modifiedApp.displayName || ""}
                      onChange={(e) => setModifiedApp({...modifiedApp, displayName: e.target.value})}
                      InputProps={{
                        disableUnderline: true,
                        style: { 
                          fontFamily: 'monospace', 
                          fontSize: '0.875rem', 
                          padding: '0 4px', 
                          width: '100%'
                        },
                        endAdornment: (
                          <EditIcon fontSize="small" sx={{ opacity: 0.6, color: 'primary.main' }} />
                        )
                      }}
                      fullWidth
                      placeholder="Application display name"
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Object ID:
                  </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      width: {
                        xs: '60%',  // On extra small screens
                        sm: '70%'   // On small screens and larger
                      },
                      maxWidth: '70%' 
                    }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        flexGrow: 1 // This will make the text take available space
                      }}
                    >
                      {app.id || "N/A"}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopyToClipboard(app.id || "")} sx={{ ml: 'auto' }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Application ID (clientId):
                  </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      width: {
                        xs: '60%',  // On extra small screens
                        sm: '70%'   // On small screens and larger
                      },
                      maxWidth: '70%' 
                    }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        flexGrow: 1 // This will make the text take available space
                      }}
                    >
                      {app.appId || "N/A"}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopyToClipboard(app.appId || "")} sx={{ ml: 'auto' }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {spLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">Fetching Service Principal ID...</Typography>
                  </Stack>
                ) : servicePrincipalId ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="medium">
                      Service Principal ID:
                    </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: '#f5f5f5', 
                        p: 1, 
                        borderRadius: 1, 
                        width: {
                          xs: '60%',  // On extra small screens
                          sm: '70%'   // On small screens and larger
                        },
                        maxWidth: '70%' 
                      }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          flexGrow: 1 // This will make the text take available space
                        }}
                      >
                        {servicePrincipalId}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCopyToClipboard(servicePrincipalId)} sx={{ ml: 'auto' }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="error">
                    Service Principal not found
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="medium">
                    Tenant ID:
                  </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      width: {
                        xs: '60%',  // On extra small screens
                        sm: '70%'   // On small screens and larger
                      },
                      maxWidth: '70%' 
                    }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        flexGrow: 1 // This will make the text take available space
                      }}
                    >
                      {tenantId || "N/A"}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopyToClipboard(tenantId)} sx={{ ml: 'auto' }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={updateApplication}
                  disabled={!modifiedApp.displayName || modifiedApp.displayName === app.displayName}
                >
                  Update Display Name
                </Button>
              </Box>           
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 2) REDIRECT URIs */}
      <Accordion
        sx={{
          border: '1px solid #e8eaf6',
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
            borderBottom: '1px solid #c5cae9',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white">
                <path d="M17 7h-4v2h4c1.1 0 2 .9 2 2s-.9 2-2 2h-3v2h3c2.21 0 4-1.79 4-4s-1.79-4-4-4zm-8 8h2V9H9v2H7v2h2v2zm-2-8v2h5V7H7z"/>
              </svg>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593",
                letterSpacing: "0.01em"
              }}
            >
              Redirect URIs
            </Typography>
            <Chip 
              size="small"
              label="Configuration" 
              sx={{ 
                ml: 2,
                backgroundColor: '#c5cae9',
                color: '#283593',
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}>
          <Box sx={{ backgroundColor: "#fafbff", borderRadius: 1 }}> {/* Updated background */}
            <Typography variant="body1" sx={{ mb: 2 }}>
              Redirect URIs are endpoints where users will be sent after authentication. These must match exactly what your application uses.
            </Typography>
            
            {/* Visual explanation of URI types with expandable security tips */}
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              backgroundColor: '#f8f8ff', 
              border: '1px solid #ddddff', 
              borderRadius: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box sx={{ color: '#3f51b5', mr: 2, mt: 0.5 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 7h-4v2h4c1.1 0 2 .9 2 2s-.9 2-2 2h-3v2h3c2.21 0 4-1.79 4-4s-1.79-4-4-4zm-8 8h2V9H9v2H7v2h2v2zm-2-8v2h5V7H7z"/>
                  </svg>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    <strong>Web Redirect URIs:</strong> For traditional web applications that run on a server (e.g., <code>https://myapp.com/auth/callback</code>)<br />
                    <strong>SPA Redirect URIs:</strong> For JavaScript single-page applications running in a browser (also HTTPS URLs)<br />
                    <strong>Mobile/Native URIs:</strong> For mobile or desktop apps (e.g., <code>myapp://callback</code> or <code>https://login.microsoftonline.com/common/oauth2/nativeclient</code>)
                  </Typography>
                  
                  {/* Security tips link */}
                  <Button 
                    onClick={() => setShowSecurityTips(!showSecurityTips)}
                    startIcon={<SecurityIcon fontSize="small" />}
                    size="small"
                    sx={{ mt: 1.5, textTransform: 'none' }}
                    color="warning"
                  >
                    {showSecurityTips ? 'Hide Security Best Practices' : 'Show Security Best Practices'}
                  </Button>
                </Box>
              </Box>
              
              {/* Collapsible security tips */}
              {showSecurityTips && (
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  backgroundColor: '#fff8e1', 
                  borderLeft: '4px solid #ffb300',
                  borderRadius: '0 4px 4px 0'
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                    Security Best Practices:
                  </Typography>
                  <Typography variant="body2">
                    • Always use HTTPS URLs for production applications<br />
                    • Be as specific as possible (include the full path)<br />
                    • Never use wildcards in production redirect URIs<br />
                    • Localhost URIs should only be used for development
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Platform Management Section */}
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: '#e8f0fe', 
              border: '1px solid #c0d6ff', 
              borderRadius: 1
            }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {hasWebPlatform && hasSpaPlatform ? (
                  <>
                    This application is configured for <strong>both Web and SPA</strong> platforms. You can manage both types of redirect URIs below.
                  </>
                ) : hasWebPlatform ? (
                  <>
                    This application is registered as a <strong>Web</strong> app.
                  </>
                ) : hasSpaPlatform ? (
                  <>
                    This application is registered as an <strong>SPA</strong> app.
                  </>
                ) : (
                  <>
                    This application has <strong>no platforms configured</strong>.
                  </>
                )}
              </Typography>
            </Box>

            {/* Add Missing Platform Section - SIMPLIFIED */}
            {(!hasWebPlatform || !hasSpaPlatform) && (
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                backgroundColor: '#fff3e0', 
                border: '1px dashed #ffb74d', 
                borderRadius: 1
              }}>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                  Add Missing Platform Type
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {!hasWebPlatform && !hasSpaPlatform ? (
                    <>
                      You can add either Web or SPA platform to enable authentication.
                    </>
                  ) : (
                    <>
                      You can add the <strong>{hasWebPlatform ? "SPA" : "Web"}</strong> platform to support both authentication types.
                    </>
                  )}
                  
                  {hasWebPlatform && !hasSpaPlatform && (
                    <Box component="span" sx={{ display: "block", mt: 1 }}>
                      <strong>When to add SPA platform:</strong> Add this if your web app also includes browser-based JavaScript components that need to authenticate directly from the browser without involving your backend.
                    </Box>
                  )}
                  
                  {hasSpaPlatform && !hasWebPlatform && (
                    <Box component="span" sx={{ display: "block", mt: 1 }}>
                      <strong>When to add Web platform:</strong> Add this if your SPA will be supplemented with a server-side component that can securely store client secrets and handle token exchanges.
                    </Box>
                  )}
                  
                  {!hasWebPlatform && !hasSpaPlatform && (
                    <Box component="span" sx={{ display: "block", mt: 1 }}>
                      <strong>Platform types:</strong> Web platforms are for server-side applications with client secrets, while SPA platforms are for browser-based JavaScript applications that cannot securely store secrets.
                    </Box>
                  )}
                </Typography>
                
                {addingPlatform ? (
                  <Box sx={{ mt: 2 }}>
                    {/* Platform Type Selection - Only show when no platforms exist */}
                    {!hasWebPlatform && !hasSpaPlatform && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Choose Platform Type:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <Button
                            variant={selectedPlatformType === 'web' ? 'contained' : 'outlined'}
                            onClick={() => setSelectedPlatformType('web')}
                            startIcon={
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v10z"/>
                              </svg>
                            }
                            sx={{ minWidth: 140 }}
                          >
                            Web App
                          </Button>
                          <Button
                            variant={selectedPlatformType === 'spa' ? 'contained' : 'outlined'}
                            onClick={() => setSelectedPlatformType('spa')}
                            startIcon={
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                              </svg>
                            }
                            sx={{ minWidth: 140 }}
                          >
                            SPA
                          </Button>
                        </Box>
                        
                        {/* Platform explanation */}
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: selectedPlatformType === 'web' ? '#e8f5e9' : '#e3f2fd', 
                          borderRadius: 1,
                          border: `1px solid ${selectedPlatformType === 'web' ? '#81c784' : '#90caf9'}`
                        }}>
                          <Typography variant="body2">
                            {selectedPlatformType === 'web' ? (
                              <>
                                <strong>Web App:</strong> Server-side application that can securely store client secrets. 
                                Best for traditional web applications, server-side rendered apps, and backend APIs.
                              </>
                            ) : (
                              <>
                                <strong>Single Page App (SPA):</strong> Browser-based JavaScript application that cannot securely store secrets. 
                                Best for React, Angular, Vue.js applications and other client-side frameworks.
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    <TextField
                      label={`${
                        !hasWebPlatform && !hasSpaPlatform 
                          ? `${selectedPlatformType.toUpperCase()} Redirect URI`
                          : `${hasWebPlatform ? "SPA" : "Web"} Redirect URI`
                      }`}
                      placeholder={
                        !hasWebPlatform && !hasSpaPlatform 
                          ? selectedPlatformType === 'web' 
                            ? "https://mywebapp.com/auth/callback" 
                            : "https://myspa.com/auth"
                          : hasWebPlatform 
                            ? "https://myspa.com/auth" 
                            : "https://mywebapp.com/auth/callback"
                      }
                      fullWidth
                      value={newPlatformUri}
                      onChange={(e) => setNewPlatformUri(e.target.value)}
                      error={!!platformError}
                      helperText={platformError || (
                        !hasWebPlatform && !hasSpaPlatform && selectedPlatformType === 'web'
                          ? "Web apps typically use callback paths like /auth/callback or /signin-oidc"
                          : !hasWebPlatform && !hasSpaPlatform && selectedPlatformType === 'spa'
                          ? "SPAs often redirect to the root path / or /auth"
                          : ""
                      )}
                      sx={{ mb: 2 }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <Button 
                        variant="outlined" 
                        onClick={handleCancelAddPlatform}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="contained"
                        color="primary"
                        onClick={() => handleAddPlatform(
                          !hasWebPlatform && !hasSpaPlatform 
                            ? selectedPlatformType 
                            : hasWebPlatform ? 'spa' : 'web'
                        )}
                        disabled={!newPlatformUri.trim()}
                      >
                        Add {
                          !hasWebPlatform && !hasSpaPlatform 
                            ? selectedPlatformType.toUpperCase()
                            : hasWebPlatform ? "SPA" : "Web"
                        } Platform
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setAddingPlatform(true)}
                  >
                    {!hasWebPlatform && !hasSpaPlatform 
                      ? "Configure First Platform"
                      : `Add ${hasWebPlatform ? "SPA" : "Web"} Platform`
                    }
                  </Button>
                )}
              </Box>
            )}            

            {/* WEB redirect URIs */}
            {hasWebUris && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                  Web Redirect URIs
                </Typography>
                {modifiedApp.web?.redirectUris?.map((uri, index) => (
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    key={`web-${index}`}
                    sx={{ mb: 1 }}
                  >
                    <TextField
                      label={`Web Redirect URI ${index + 1}`}
                      variant="outlined"
                      fullWidth
                      value={uri}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setModifiedApp((prev) => {
                          const newUris = [...(prev.web?.redirectUris || [])];
                          newUris[index] = newValue;
                          return {
                            ...prev,
                            web: { ...prev.web, redirectUris: newUris },
                          };
                        });
                      }}
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setModifiedApp((prev) => {
                          const newUris = [...(prev.web?.redirectUris || [])];
                          newUris.splice(index, 1);
                          return {
                            ...prev,
                            web: { ...prev.web, redirectUris: newUris },
                          };
                        });
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
                {/* Action buttons with improved placement and styling */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  mt: 1, 
                  pt: 1, 
                  borderBottom: '1px solid #e0e0e0', 
                  mb: 2,
                  pb: 2
                }}>
                  {/* Left side - Add action */}
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setModifiedApp((prev) => ({
                        ...prev,
                        web: {
                          ...prev.web,
                          redirectUris: [...(prev.web?.redirectUris || []), ""],
                        },
                      }));
                    }}
                  >
                    Add Web Redirect URI
                  </Button>
                  
                  {/* Right side - Apply changes */}
                  <Button 
                    variant="contained" 
                    color="success" 
                    startIcon={<SaveIcon />}
                    onClick={updateApplication}
                  >
                    Apply Changes
                  </Button>
                </Box>
              </>
            )}            

            {/* SPA redirect URIs */}
            {hasSpaUris && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                  SPA Redirect URIs
                </Typography>
                {modifiedApp.spa?.redirectUris?.map((uri, index) => (
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    key={`spa-${index}`}
                    sx={{ mb: 1 }}
                  >
                    <TextField
                      label={`SPA Redirect URI ${index + 1}`}
                      variant="outlined"
                      fullWidth
                      value={uri}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setModifiedApp((prev) => {
                          const newUris = [...(prev.spa?.redirectUris || [])];
                          newUris[index] = newValue;
                          return {
                            ...prev,
                            spa: { ...prev.spa, redirectUris: newUris },
                          };
                        });
                      }}
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setModifiedApp((prev) => {
                          const newUris = [...(prev.spa?.redirectUris || [])];
                          newUris.splice(index, 1);
                          return {
                            ...prev,
                            spa: { ...prev.spa, redirectUris: newUris },
                          };
                        });
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  mt: 1, 
                  pt: 1, 
                }}>
                  {/* Left side - Add action - FIXED CODE */}
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setModifiedApp((prev) => ({
                        ...prev,
                        spa: {
                          ...prev.spa,
                          redirectUris: [...(prev.spa?.redirectUris || []), ""],
                        },
                      }));
                    }}
                  >
                    Add SPA Redirect URI
                  </Button>
                  
                  {/* Right side - Apply changes */}
                  <Button 
                    variant="contained" 
                    color="success" 
                    startIcon={<SaveIcon />}
                    onClick={updateApplication}
                  >
                    Apply Changes
                  </Button>
                </Box>
              </>
            )}
            
            {/* CONDITIONAL APPLY BUTTON - Only when platforms exist but all URIs removed */}
            {(!hasWebUris && !hasSpaUris) && 
            (JSON.stringify(modifiedApp.web?.redirectUris) !== JSON.stringify(app.web?.redirectUris) ||
              JSON.stringify(modifiedApp.spa?.redirectUris) !== JSON.stringify(app.spa?.redirectUris)) && (
              <Box sx={{ 
                mt: 2,
                pt: 2,
                borderTop: '1px solid #ffb74d',
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#fff8e1',
                borderRadius: 1,
                p: 2
              }}>
                <Button 
                  variant="contained" 
                  color="warning" 
                  startIcon={<SaveIcon />}
                  onClick={updateApplication}
                  sx={{ 
                    minWidth: 160,
                    fontWeight: 600,
                    mb: 1
                  }}
                >
                  Apply Changes (Remove All URIs)
                </Button>
                
                {/* Simple reminder message */}
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  💡 Tip: Refresh the page after applying changes to see the updated interface
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 3) SECRETS & CERTIFICATES */}
      <Accordion
        sx={{
          border: '1px solid #e8eaf6',
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
            borderBottom: '1px solid #c5cae9',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593",
                letterSpacing: "0.01em"
              }}
            >
              Secrets & Certificates
            </Typography>
            <Chip 
              size="small"
              label="Security" 
              sx={{ 
                ml: 2,
                backgroundColor: '#c5cae9',
                color: '#283593',
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}>
          <Box sx={{ backgroundColor: "#fafbff", borderRadius: 1 }}> {/* Updated background */}
          <Typography variant="body1" sx={{ mb: 2 }}>
            View and manage the client secrets and certificates for this application. These credentials allow the application to authenticate directly without a user.
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 2, 
            backgroundColor: '#fffbeb', 
            border: '1px dashed #ffd54f', 
            borderRadius: 1
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SecurityIcon color="warning" />
              <Typography variant="body2" fontWeight="medium">
                Client credentials are sensitive. Treat them like passwords!
              </Typography>
            </Stack>
            
            <Button
              variant="contained"
              color="secondary"
              startIcon={<VpnKeyIcon />}
              onClick={() => setClientCredsModalOpen(true)}
              sx={{
                fontWeight: 500,
                px: 2,
                backgroundColor: '#f9a825',
                '&:hover': {
                  backgroundColor: '#f57f17'
                }
              }}
            >
              Show Credentials
            </Button>
          </Box>    
        </Box>
        </AccordionDetails>
      </Accordion>

      {/* 4) SUPPORTED ACCOUNT TYPES */}
      <Accordion
        sx={{
          border: '1px solid #e8eaf6',
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
            borderBottom: '1px solid #c5cae9',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#3f51b5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <PeopleIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#283593",
                letterSpacing: "0.01em"
              }}
            >
              Supported Account Types
            </Typography>
            <Chip 
              size="small"
              label="Access" 
              sx={{ 
                ml: 2,
                backgroundColor: '#c5cae9',
                color: '#283593',
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: "#fafbff", p: 3 }}>
          <Box sx={{ backgroundColor: "#fafbff", borderRadius: 1 }}> {/* Updated background */}
            <Typography variant="body1" sx={{ mb: 2 }}>
              Configure which accounts can sign in to this application. This affects compatibility with other tenants and the Microsoft identity platform.
            </Typography>
            
            {/* Visual explanation of account types */}
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              backgroundColor: '#f0fff0', 
              border: '1px solid #c8e6c9', 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center'
            }}>
              <PeopleIcon sx={{ color: '#4caf50', mr: 2 }} />
              <Typography variant="body2">
                <strong>Single tenant:</strong> Only users in your organization<br />
                <strong>Multi-tenant:</strong> Users in any Entra ID organization
              </Typography>
            </Box>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="account-type-select-label">Account Type</InputLabel>
              <Select
                labelId="account-type-select-label"
                value={modifiedApp.signInAudience || ""}
                label="Account Type"
                onChange={(e) =>
                  setModifiedApp((prev) => ({
                    ...prev,
                    signInAudience: e.target.value as Application["signInAudience"],
                  }))
                }
              >
                <MenuItem value="AzureADMyOrg">
                  Accounts in this organizational directory only
                </MenuItem>
                <MenuItem value="AzureADMultipleOrgs">
                  Accounts in any organizational directory (Multitenant)
                </MenuItem>
              </Select>
            </FormControl>

            {/* Current selection summary */}
            <Box sx={{ 
              mb: 3, 
              p: 1.5, 
              backgroundColor: '#e8f5e9', 
              borderLeft: '4px solid #4caf50',
              borderRadius: '0 4px 4px 0'
            }}>
              <Typography variant="body2">
                <strong>Current selection:</strong> {modifiedApp.signInAudience === 'AzureADMyOrg' 
                  ? 'Single-tenant (this organization only)' 
                  : 'Multi-tenant (any Entra ID directory)'}
              </Typography>
            </Box>

            {/* Apply changes button in its own container */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              pt: 1,
              borderTop: '1px solid #e0e0e0' 
            }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={updateApplication}
              >
                Apply Changes
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

    </>
  );
};

export default GeneralSettings;
